import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { 
  ChevronDown, 
  Plus,
  Building2,
  Check,
  Crown,
  Shield,
  User,
  RefreshCw
} from 'lucide-react';

const WorkspaceSelector = () => {
  const {
    workspaces,
    currentWorkspace,
    switchWorkspace,
    createWorkspace,
    clearCurrentWorkspace,
    canCreateWorkspace,
    subscriptionInfo
  } = useWorkspace();

  const [isOpen, setIsOpen] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [newWorkspaceDescription, setNewWorkspaceDescription] = useState('');

  const atLimit = subscriptionInfo && subscriptionInfo.can_create_workspace === false;

  /** Only owners/admins (and super admins) manage account workspaces & plan upgrades; members should not see + New / upgrade prompts here. */
  const canManageAccountWorkspaces =
    subscriptionInfo?.is_super_admin ||
    ['owner', 'admin'].includes(String(currentWorkspace?.user_role || '').toLowerCase());

  const handleCreateWorkspace = async (e) => {
    e.preventDefault();
    if (!newWorkspaceName.trim()) return;

    const result = await createWorkspace(newWorkspaceName.trim(), newWorkspaceDescription.trim());
    if (result.success) {
      setNewWorkspaceName('');
      setNewWorkspaceDescription('');
      setShowCreateForm(false);
      setIsOpen(false);
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'owner':
        return <Crown className="w-3 h-3 text-yellow-500" />;
      case 'admin':
        return <Shield className="w-3 h-3 text-blue-500" />;
      default:
        return <User className="w-3 h-3 text-gray-500" />;
    }
  };

  if (!currentWorkspace) {
    return (
      <div className="flex items-center space-x-2">
        <div className="w-8 h-8 bg-gray-200 rounded-lg flex items-center justify-center">
          <Building2 className="w-4 h-4 text-gray-500" />
        </div>
        <span className="text-sm text-gray-500">No workspace</span>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Current Workspace Display */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors duration-200 border border-gray-200 bg-white"
      >
        <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
          <Building2 className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 text-left">
          <div className="text-sm font-medium text-gray-900 truncate max-w-32">
            {currentWorkspace.name}
          </div>
          <div className="flex items-center space-x-1">
            {getRoleIcon(currentWorkspace.user_role)}
            <span className="text-xs text-gray-500 capitalize">
              {currentWorkspace.user_role}
            </span>
          </div>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-80 bg-white rounded-xl shadow-lg border border-gray-200 z-50">
          <div className="p-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-900">Workspaces</h3>
              <div className="flex items-center space-x-2">
                {/* Create workspace: main admin context (workspace 1) + owner/admin only */}
                {currentWorkspace.id === 1 && canManageAccountWorkspaces && (
                  <button
                    onClick={() => setShowCreateForm(!showCreateForm)}
                    className="flex items-center space-x-1 px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded-md transition-colors duration-200"
                  >
                    <Plus className="w-3 h-3" />
                    <span>New</span>
                  </button>
                )}
                {/* Switch workspace button */}
                <button
                  onClick={() => {
                    clearCurrentWorkspace();
                    setIsOpen(false);
                  }}
                  className="flex items-center space-x-1 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50 rounded-md transition-colors duration-200"
                  title="Switch to different workspace"
                >
                  <RefreshCw className="w-3 h-3" />
                  <span>Switch</span>
                </button>
              </div>
            </div>

            {/* Create Workspace Form - workspace 1 + owner/admin */}
            {currentWorkspace.id === 1 && canManageAccountWorkspaces && showCreateForm && (
              <form onSubmit={handleCreateWorkspace} className="mb-4 p-3 bg-gray-50 rounded-lg">
                <div className="space-y-3">
                  <div>
                    <input
                      type="text"
                      placeholder="Workspace name"
                      value={newWorkspaceName}
                      onChange={(e) => setNewWorkspaceName(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      autoFocus
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      placeholder="Description (optional)"
                      value={newWorkspaceDescription}
                      onChange={(e) => setNewWorkspaceDescription(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div className="flex space-x-2">
                    <button
                      type="submit"
                      className="flex-1 px-3 py-2 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors duration-200"
                    >
                      Create
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowCreateForm(false);
                        setNewWorkspaceName('');
                        setNewWorkspaceDescription('');
                      }}
                      className="flex-1 px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors duration-200"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </form>
            )}

            {/* Workspaces List */}
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {workspaces.map((workspace) => (
                <button
                  key={workspace.id}
                  onClick={() => {
                    switchWorkspace(workspace);
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors duration-200 ${
                    workspace.id === currentWorkspace.id
                      ? 'bg-blue-50 border border-blue-200'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                    <Building2 className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1 text-left">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {workspace.name}
                    </div>
                    <div className="flex items-center space-x-1">
                      {getRoleIcon(workspace.user_role)}
                      <span className="text-xs text-gray-500 capitalize">
                        {workspace.user_role}
                      </span>
                    </div>
                  </div>
                  {workspace.id === currentWorkspace.id && (
                    <Check className="w-4 h-4 text-blue-600" />
                  )}
                </button>
              ))}
            </div>

            {/* Plan limit / Upgrade CTA — only for users who manage billing (not members/agents/viewers) */}
            {atLimit && canManageAccountWorkspaces && (
              <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs text-amber-800 mb-1">Plan limit reached</p>
                <Link
                  to="/settings?tab=plans"
                  className="text-xs font-medium text-amber-700 hover:text-amber-900"
                >
                  Upgrade to create more workspaces
                </Link>
              </div>
            )}

            {/* Empty State */}
            {workspaces.length === 0 && !showCreateForm && (
              <div className="text-center py-8">
                <Building2 className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500 mb-3">No workspaces yet</p>
                {atLimit && canManageAccountWorkspaces ? (
                  <Link
                    to="/settings?tab=plans"
                    className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Upgrade your plan to create a workspace
                  </Link>
                ) : (
                  !atLimit &&
                  currentWorkspace.id === 1 &&
                  canManageAccountWorkspaces && (
                    <button
                      onClick={() => setShowCreateForm(true)}
                      className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Create your first workspace
                    </button>
                  )
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default WorkspaceSelector;
